"use client";

import { AspectRatio } from "@ui/components/core/AspectRatio";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";
import { useCallback, useRef } from "react";
import { BlockAudioPlayerWithTranscriptLoader } from "./index";
import type { AudioPlayerRef, PreparedSliderItem } from "./types";

interface AudioPlayerListProps {
  buttonStyle?: "default" | "icon" | null;
  fadeUnspokenWords?: boolean;
  preparedItems: PreparedSliderItem[];
  showMetadata?: boolean;
}

export function AudioPlayerList({ preparedItems, showMetadata, fadeUnspokenWords, buttonStyle }: AudioPlayerListProps) {
  const audioRefs = useRef<(AudioPlayerRef | null)[]>([]);

  // Stop all other players when a new one starts
  const stopAllOthers = useCallback((exceptIndex: number) => {
    audioRefs.current.forEach((ref, index) => {
      if (ref && index !== exceptIndex && ref.isPlaying()) {
        ref.pause();
      }
    });
  }, []);

  const aspectRatio = showMetadata ? "3x4" : "1x1";

  return (
    <ScrollGrid
      items={preparedItems}
      renderItem={(item, itemIndex) => {
        if (!item.audioUrl) return null;

        return (
          <AspectRatio ratio={aspectRatio}>
            <BlockAudioPlayerWithTranscriptLoader
              audioUrl={item.audioUrl}
              backgroundImage={item.backgroundImage}
              buttonStyle={item.buttonStyle || buttonStyle}
              compact={!!showMetadata}
              enableLazyLoading={!!item.sampleId}
              fadeUnspokenWords={fadeUnspokenWords}
              iconNode={item.iconNode}
              onPlayingChange={(isPlaying: boolean) => {
                if (isPlaying) {
                  stopAllOthers(itemIndex);
                }
              }}
              ref={(ref: AudioPlayerRef | null) => {
                audioRefs.current[itemIndex] = ref;
              }}
              sampleId={item.sampleId}
              showMetadata={showMetadata}
              showPlayButton
              subtitle={item.subtitle}
              title={item.title}
            />
          </AspectRatio>
        );
      }}
    />
  );
}
